/**
 * `PUT /me/equipment`: where the person trains, and what they train with.
 *
 * ── ABSENT `place` KEEPS; `null` CLEARS ─────────────────────────────────────
 * The two editors send different bodies on purpose. The place editor sends
 * the place plus the stored checklist; the checklist editor sends
 * `available` ONLY (`checklistInput`), because sending `place: current ??
 * null` from it would wipe the stored place on every checklist save.
 */
import { catalogFiltersOptions } from "@repo/client/data/catalog";
import { equipmentOptions } from "@repo/client/data/profile";
import {
  type EquipmentAvailabilityInput,
  type EquipmentPlace,
  equipmentAvailabilityInput,
} from "@repo/contract/training";
import {
  "profile-editors.equipment_empty" as equipmentEmpty,
  "onboarding.equipment.question" as equipmentQuestion,
  "profile-editors.error_field" as errorField,
  "profile-editors.eyebrow_equipment" as eyebrowEquipment,
  "onboarding.equipment_place.question" as placeQuestion,
  "intake.why.equipment_place.long" as placeWhy,
} from "@repo/i18n";
import { getLocale } from "@repo/i18n/runtime";
import { useApi } from "../../../data/api";
import { useDataQuery } from "../../../data/query-client";
import { ChoiceForm } from "./choice-form";
import {
  type EditorHost,
  EditorShell,
  useSaveFlow,
  useSectionRead,
} from "./editor-shell";
import { EquipmentForm } from "./equipment-form";
import { placeOptions } from "./options";
import { checklistInput, placeInput } from "./payloads";
import { useEditorForm } from "./use-editor-form";
import { useEquipmentMutation } from "./use-editor-mutation";
import { NO_ERRORS } from "./validate";

interface EditorProps {
  readonly host: EditorHost;
}

export function PlaceEditor({ host }: EditorProps) {
  const api = useApi();
  const read = useSectionRead(equipmentOptions(api));
  const flow = useSaveFlow(useEquipmentMutation(), host);
  const stored = read.data?.place ?? null;
  const form = useEditorForm({
    onSubmit: (payload: EquipmentAvailabilityInput) => flow.save(payload),
    schema: equipmentAvailabilityInput,
    server: stored,
    toDraft: (place: EquipmentPlace | null) => place,
    toPayload: (place: EquipmentPlace | null) => placeInput(read.data, place),
  });
  return (
    <EditorShell
      disabled={form.draft === null || form.draft === stored}
      eyebrow={eyebrowEquipment()}
      failure={
        flow.failure ??
        (Object.keys(form.errors).length > 0 ? errorField() : null)
      }
      host={host}
      loadFailed={read.loadFailed}
      loading={read.loading}
      onSave={form.submit}
      pending={flow.pending}
      title={placeQuestion()}
    >
      <ChoiceForm
        helper={placeWhy()}
        label={placeQuestion()}
        onChange={form.setDraft}
        options={placeOptions()}
        value={form.draft}
      />
    </EditorShell>
  );
}

/** The catalogue is loaded in `en` or `pt`, the app in `en-US` or `pt-BR`. */
const catalogueLocale = () => (getLocale().startsWith("pt") ? "pt" : "en");

export function EquipmentEditor({ host }: EditorProps) {
  const api = useApi();
  const read = useSectionRead(equipmentOptions(api));
  const filters = useDataQuery(catalogFiltersOptions(api, catalogueLocale()));
  const flow = useSaveFlow(useEquipmentMutation(), host);
  const stored = read.data?.available ?? null;
  const form = useEditorForm({
    onSubmit: (payload: EquipmentAvailabilityInput) => flow.save(payload),
    precheck: (available: number[]) =>
      available.length === 0 ? { available: "empty" } : NO_ERRORS,
    schema: equipmentAvailabilityInput,
    server: stored,
    toDraft: (available: readonly number[] | null) => [...(available ?? [])],
    toPayload: (available: number[]) => checklistInput(available),
  });
  return (
    <EditorShell
      disabled={form.draft.length === 0}
      eyebrow={eyebrowEquipment()}
      failure={
        flow.failure ??
        (form.errors.available === undefined &&
        Object.keys(form.errors).length > 0
          ? errorField()
          : null)
      }
      host={host}
      loadFailed={read.loadFailed || filters.isError}
      loading={read.loading || filters.isPending}
      onSave={form.submit}
      pending={flow.pending}
      title={equipmentQuestion()}
    >
      <EquipmentForm
        error={
          form.errors.available === undefined ? undefined : equipmentEmpty()
        }
        onChange={form.setDraft}
        rows={filters.data?.equipment ?? []}
        value={form.draft}
      />
    </EditorShell>
  );
}
